import React from 'react'
import { Box, Chip, Paper, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

/** Side-by-side Dashboard panels share one height, so a long list scrolls instead of stretching the page. */
export const DASHBOARD_PANEL_HEIGHT = 380

export interface DashboardPanelProps {
  title: string
  count?: number
  /** Right side of the header, e.g. a "View all" link. */
  action?: React.ReactNode
  children: React.ReactNode
  'data-testid'?: string
}

export default function DashboardPanel({ title, count, action, children, ...rest }: DashboardPanelProps) {
  return (
    <Paper
      elevation={1}
      data-testid={rest['data-testid']}
      sx={{
        height: DASHBOARD_PANEL_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        backgroundImage: 'none',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Chip
            label={count}
            size="small"
            sx={(t) => ({ backgroundColor: alpha(t.palette.primary.main, 0.15), color: 'primary.main', fontWeight: 600 })}
          />
        )}
        {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</Box>
    </Paper>
  )
}

/** Centred placeholder for an empty panel. */
export function DashboardPanelEmpty({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
        {children}
      </Typography>
    </Box>
  )
}
